<template>
  <article class="login-container">
    <section class="main">
      <section class="logo">
        <img class="ifield-logo" src="@/assets/ifield/iFieldlogo.png" />
      </section>
      <multi-factor-auth-form
        v-bind:otp="form.otp"
        v-bind:error-message="apiError"
        class="form"
        v-on:login="onSubmit"
        v-on:cancel="$router.push('/login')"
      />
    </section>
    <login-footer />
    <loading-mask is-full-screen v-bind:isLoading="isLoggingIn" />
  </article>
</template>

<script>
import { RestApiClient } from 'ifield-core-components'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { useInitializeAtLoggedIn } from '@/composables/UseInitializeAtLoggedIn.js'
import MultiFactorAuthForm from '@/components/login/AppMultiFactorAuthForm.vue'
import LoginFooter from '@/components/login/AppLoginFooter.vue'

/**
 * 現在ページのhrefを取得する
 * @returns {string} href
 */
const getCurrentHref = () => {
  const url = new URL(location.href)
  const urlString = url.toString()
  const replaceHolder =
    url.search || (urlString[urlString.length - 1] === '?' ? '?' : '')
  return urlString.replace(replaceHolder, '')
}

/**
 * 多要素認証を実行する画面
 */
export default {
  name: 'TheMultiFactorAuth',
  components: {
    MultiFactorAuthForm,
    LoginFooter,
  },
  inheritAttrs: false,
  setup() {
    const { handleApiError } = useHandleApiError()
    const { onAfterLoginSuccess } = useInitializeAtLoggedIn()
    return { handleApiError, onAfterLoginSuccess }
  },
  data: function () {
    return {
      apiError: null,
      form: {
        otp: null,
      },
      isLoggingIn: false,
    }
  },
  methods: {
    /**
     * submitボタン押下時の処理
     * @param {Object} value フォーム入力値({ loginCode, password })
     */
    onSubmit: async function (value) {
      this.form = { ...value }
      this.$_showLoading()
      this.apiError = null

      try {
        // ログインAPIを実行する
        const loginData = await this.$_login()
        // storeにログインユーザーの情報を保存する
        this.$store.dispatch('saveLogin', loginData)
        // ログイン後処理
        await this.onAfterLoginSuccess()
        // redirect先が指定されていればそちらのページを起動
        const path = this.$route.query.redirect || '/'
        this.$router.push(path)
      } catch (error) {
        if (error.message) {
          this.apiError = error.message
        } else {
          this.$log.error('unexpected error', error)
          this.apiError = error
        }
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * フォームの情報を使ってログインAPI実行
     */
    $_login: async function () {
      const url = RestApiClient.createApiUrl('login/verify_otp')
      const body = {
        otp: this.form.otp,
      }

      try {
        const json = await RestApiClient.executePostApi(url, body)
        return json.data
      } catch (error) {
        await this.handleApiError(error, this.$_onLoginFailure, true)
      }
    },

    /**
     * ログイン失敗時処理
     * @param {Response|Error} error APIエラー
     */
    $_onLoginFailure: async function (error) {
      let message

      if (error instanceof Response) {
        // 認証に失敗した場合
        const json = await error.json()
        // リダイレクトURLが設定されている場合
        const redirectError =
          json.errors && json.errors.find((error) => error.redirect_url)
        if (redirectError) {
          this.$_redirectToChangePassword(json.message, redirectError)
          message = '' // メッセージ部には何も表示しない
        } else if (error.status === 401) {
          // 認証情報が不正
          message =
            json.message || this.$t('theMultiFactorAuthForm.message.invalidOtp')
        } else if (error.status === 423) {
          // アカウントロック
          message =
            json.message ||
            this.$t('theMultiFactorAuthForm.message.accountLock')
        } else {
          message =
            json.message ||
            this.$t('theMultiFactorAuthForm.message.serverError')
        }
      } else {
        message = `${this.$t('theMultiFactorAuthForm.message.failedToLogin')}(${error})`
      }

      throw new Error(message)
    },

    /**
     * パスワード変更画面にリダイレクトする
     * @param {String} title タイトルエラーメッセージ
     * @param {Object} error リダイレクトを促しているエラーオブジェクト
     */
    $_redirectToChangePassword: function (title, error) {
      this.$alert
        .fire({
          icon: 'warning',
          title: title,
          text: error.message,
        })
        .then(() => {
          const anchor = document.createElement('a')
          const url = new URL(error.redirect_url)
          url.searchParams.append('back_url', getCurrentHref())
          anchor.href = url.toString()
          anchor.click()
        })
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoggingIn = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoggingIn = false
    },
  },
}
</script>

<style lang="scss" scoped>
.login-container {
  background-color: #fafafa;
  width: 100%;
  height: 100%;
  padding-bottom: 32px;
  display: grid;
  grid-template-rows: 1fr max-content 0;
}

.main {
  flex-shrink: 0;
  margin: auto;
  /* 親のGridコンテナ内で垂直方向中央に配置します */
  align-self: center;
  display: grid;
  grid-template-columns: max-content;
  grid-template-rows: 80px 1fr;
  row-gap: 4px;
}

.logo {
  grid-column: 1;
  grid-row: 1;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;

  .ifield-logo {
    height: 80px;
    position: relative;
    left: -18px;
  }
}

.form {
  grid-column: 1;
  grid-row: 2;
}
</style>
